import { InjectionToken } from "@angular/core";

/**
 * Interface for managing the authenticated principal.
 * Mirrors the pattern of AuthenticationService used in production apps:
 * the principal is persisted in localStorage and recovered on page reload.
 *
 * Host apps implement this interface and provide it via PRINCIPAL_SERVICE.
 * The library uses it to scope agent thread searches to the current user
 * via metadata.user in the Agents API.
 *
 * @example
 * providers: [{ provide: PRINCIPAL_SERVICE, useClass: MyPrincipalService }]
 */
export interface IPrincipalService {
  /** Returns the current user's name (used as metadata.user in agent thread records), or null. */
  getPrincipal(): string | null;

  /** Returns the authentication type: "api_key" | "impersonation_key" | "geo_key" | "token". */
  getAuthType(): string | null;

  /**
   * Returns the query parameter name to use for URL-based auth (e.g. "token", "api_key").
   * Mirrors AuthenticationService.getAuthParam().
   */
  getAuthParam(): string;

  /**
   * Returns the full query string fragment for URL-based auth (e.g. "token=xxx", "api_key=xxx").
   * Mirrors AuthenticationService.getQueryAuth(). Useful for GET requests to the Vexcel API
   * and file storage endpoints that cannot use request headers.
   */
  getQueryAuth(): string;

  /** Persists the authenticated user after login (should survive page reload via localStorage). */
  setCurrentUser(user: Record<string, unknown>): void;

  /** Clears the stored user on logout. */
  clearCurrentUser(): void;
}

/** Injection token for IPrincipalService. */
export const PRINCIPAL_SERVICE = new InjectionToken<IPrincipalService>("PRINCIPAL_SERVICE");
